import { formatDateTimeDDMMYY } from '../../../utils/dateFormat';
import React from 'react';
import { Box, LinearProgress, Stack, Typography, keyframes } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { DestinationHealthEnvelope, DestinationHealthStatus, Transcoder, PullOutputStatusEnvelope, HlsSessionStatusEnvelope } from '../../../services/streamsService';

export const isPullOutput = (t: Transcoder) => t.isPullLink === true || t.deliveryMode === 'PULL';
export const isPullEligible = (t: Transcoder) =>
  t.protocol !== 'hls' && !t.enableScte35 && !t.isHlsSpecial && t.videoCodec === 'copy';

const SOCIAL_PLATFORM_NAMES = [
  'facebook',
  'facebook live',
  'youtube',
  'youtube live',
  'twitch',
  'instagram',
  'instagram live',
  'tiktok',
  'steam',
  'vimeo',
  'dailymotion',
  'rumble',
  'godtube',
  'bitchute',
  'onlyfans',
  'stripchat',
];

export const isSocialStreamOutput = (t: Transcoder) => {
  if ((t as any).isSocialStream === true) return true;
  const platform = String((t as any).platformDistributor || '').trim().toLowerCase();
  return t.protocol === 'rtmp'
    && t.videoCodec === 'copy'
    && (!t.audioCodec || t.audioCodec === 'copy')
    && SOCIAL_PLATFORM_NAMES.some((name) => platform === name || platform.includes(name));
};
export const isManagedServiceOutput = (t: Transcoder) => (t as any).isManagedService === true;

export const buildSrtPreviewPullUrl = (
  base: string,
  latencyMs: string,
  includePassphrase: boolean,
  pullSecret: string | null | undefined,
) => {
  const [left, right = ''] = (base || '').split('?');
  const params = new URLSearchParams(right);
  if (latencyMs.trim()) params.set('latency', latencyMs.trim());
  else params.delete('latency');
  if (includePassphrase && pullSecret) {
    params.set('passphrase', pullSecret);
    params.set('pbkeylen', '16');
  } else {
    params.delete('passphrase');
    params.delete('pbkeylen');
  }
  const qs = params.toString();
  return qs ? `${left}?${qs}` : left;
};

export const buildRistPreviewPullUrl = (
  base: string,
  bufferMs: string,
  includeStreamId: boolean,
  pullKey: string | null | undefined,
  includeSecret: boolean,
  pullSecret: string | null | undefined,
) => {
  const [left, right = ''] = (base || '').split('?');
  const params = new URLSearchParams(right);
  if (bufferMs.trim()) params.set('buffer_size', bufferMs.trim());
  else params.delete('buffer_size');
  if (includeStreamId && pullKey) params.set('stream_id', pullKey);
  else params.delete('stream_id');
  if (includeSecret && pullSecret) params.set('secret', pullSecret);
  else params.delete('secret');
  const qs = params.toString();
  return qs ? `${left}?${qs}` : left;
};

export const formatKbps = (value?: number | null) => {
  if (!value || value <= 0) return '--';
  if (value >= 1000) return `${(value / 1000).toFixed(2)} Mbps`;
  return `${value.toFixed(0)} Kbps`;
};

export const formatDateTime = (value?: string | null) => {
  return formatDateTimeDDMMYY(value);
};

export const formatBytes = (value?: number | null) => {
  if (!value || value <= 0) return '--';
  if (value >= 1024 * 1024 * 1024) return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(2)} KB`;
  return `${value} B`;
};

export const UI_STATUS_FRESH_MS = 12000;
export const UI_HLS_STATUS_FRESH_MS = 8000;
export const UI_STATUS_TRAFFIC_KBPS_MIN = 1;
export const UI_STATUS_BITRATE_KBPS_MIN = 64;

const toNum = (value: any) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
};

const parseTime = (value?: string | null) => {
  if (!value) return NaN;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : NaN;
};

const isFresh = (windowMs: number, ...values: Array<string | null | undefined>) => {
  const last = Math.max(...values.map(parseTime));
  return Number.isFinite(last) && Date.now() - last <= windowMs;
};

const firstText = (...values: Array<any>) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text && text !== '--') return text;
  }
  return null;
};

export const buildGeoLabel = (status?: PullOutputStatusEnvelope['status']) => {
  const geo = status?.currentViewer?.geo;
  if (!geo) return '--';
  return [geo.city, geo.region, geo.country].filter(Boolean).join(', ') || '--';
};

export const buildHlsGeoLabel = (session?: HlsSessionStatusEnvelope['activeSession']) => {
  if (!session) return '--';
  return [session.city, session.region, session.country].filter(Boolean).join(', ') || '--';
};

const readDestinationHealth = (
  output: Transcoder,
  runtime?: PullOutputStatusEnvelope['status'] | HlsSessionStatusEnvelope['activeSession'] | Record<string, any> | null,
): DestinationHealthEnvelope | null => {
  const directRuntime = (runtime as any)?.metrics && (runtime as any)?.status
    ? runtime as DestinationHealthEnvelope
    : null;
  const fromRuntime = (runtime as any)?.destinationHealth;
  const fromOutput = (output as any)?.destinationHealth;
  const fromStats = (output.status?.stats as any)?.destinationHealth;
  return directRuntime || fromRuntime || fromOutput || fromStats || null;
};

const readRuntimeStats = (
  output: Transcoder,
  runtime?: PullOutputStatusEnvelope['status'] | HlsSessionStatusEnvelope['activeSession'] | Record<string, any> | null,
) => ((runtime || output.status?.stats || {}) as Record<string, any>);

export const getDestinationHealthMetrics = (
  output: Transcoder,
  runtime?: PullOutputStatusEnvelope['status'] | HlsSessionStatusEnvelope['activeSession'] | Record<string, any> | null,
) => {
  const health = readDestinationHealth(output, runtime);
  if (health?.metrics) {
    return {
      ip: health.metrics.ip || null,
      trafficKbps: toNum(health.metrics.trafficKbps),
      dataMoving: health.metrics.dataMoving === true,
      validIp: health.metrics.validIp === true || Boolean(health.metrics.ip),
      positiveTraffic: health.metrics.positiveTraffic === true || toNum(health.metrics.trafficKbps) > 0,
      status: health.status,
      source: health.metrics.source || health.raw?.source || null,
      updatedAt: health.metrics.updatedAt || null,
    };
  }

  const stats = readRuntimeStats(output, runtime);
  const ip =
    stats.currentViewer?.ip ||
    stats.clientIp ||
    stats.viewerIp ||
    stats.ip ||
    stats.remoteIp ||
    null;
  const trafficKbps = Math.max(
    toNum(stats.currentKbps),
    toNum(stats.averageKbps),
    toNum(stats.peakKbps),
    toNum(stats.lastKbps),
    toNum(stats.bitrateKbps),
    toNum(stats.bitrate),
    toNum(stats.bytesPerSecond) > 0 ? (toNum(stats.bytesPerSecond) * 8) / 1000 : 0,
  );
  const freshSignal = isFresh(
    15000,
    stats.lastActivityAt,
    stats.lastSeenAt,
    stats.lastTrafficAt,
    stats.lastViewerSeen,
    stats.currentViewer?.lastSeenAt,
    stats.currentViewer?.connectedAt,
    stats.lastRequestAt,
    stats.lastPlaylistRequestAt,
    stats.lastSegmentRequestAt,
    stats.lastMediaRequestAt,
  );
  const isPull = isPullOutput(output);
  const dataMoving = stats.dataMoving === true || (
    freshSignal && (trafficKbps > 0 || (isPull && Boolean(ip)))
  );
  const validIp = Boolean(ip && String(ip).trim() && String(ip).trim() !== '--');
  const status: DestinationHealthStatus = !output.isActive ? 'disabled' : (validIp && dataMoving && (trafficKbps > 0 || isPull) ? 'connected' : 'disconnected');
  return {
    ip: validIp ? String(ip).trim() : null,
    trafficKbps,
    dataMoving,
    validIp,
    positiveTraffic: trafficKbps > 0,
    status,
    source: null,
    updatedAt:
      stats.lastActivityAt ||
      stats.lastSeenAt ||
      stats.lastViewerSeen ||
      stats.currentViewer?.lastSeenAt ||
      stats.lastRequestAt ||
      stats.lastPlaylistRequestAt ||
      stats.lastSegmentRequestAt ||
      null,
  };
};

export const getDestinationBitrateKbps = (
  output: Transcoder,
  runtime?: PullOutputStatusEnvelope['status'] | HlsSessionStatusEnvelope['activeSession'] | Record<string, any> | null,
) => {
  const health = readDestinationHealth(output, runtime);
  const stats = readRuntimeStats(output, runtime);
  return Math.max(
    toNum((runtime as any)?.currentKbps),
    toNum((runtime as any)?.averageKbps),
    toNum((runtime as any)?.peakKbps),
    toNum((runtime as any)?.bitrateKbps),
    toNum((runtime as any)?.bitrate),
    toNum(stats.currentKbps),
    toNum(stats.averageKbps),
    toNum(stats.peakKbps),
    toNum(stats.lastKbps),
    toNum(stats.bitrateKbps),
    toNum(stats.bitrate),
    toNum(stats.bytesPerSecond) > 0 ? (toNum(stats.bytesPerSecond) * 8) / 1000 : 0,
    toNum(health?.metrics?.trafficKbps),
  );
};

export const getDestinationDataBytes = (
  output: Transcoder,
  runtime?: PullOutputStatusEnvelope['status'] | HlsSessionStatusEnvelope['activeSession'] | Record<string, any> | null,
) => {
  const stats = readRuntimeStats(output, runtime);
  const health = readDestinationHealth(output, runtime);
  return Math.max(
    toNum((runtime as any)?.bytesServed),
    toNum((runtime as any)?.lastBytesServed),
    toNum((runtime as any)?.bytesOut),
    toNum((runtime as any)?.totalBytesOut),
    toNum((runtime as any)?.bytesWritten),
    toNum((runtime as any)?.totalBytesWritten),
    toNum((runtime as any)?.bytesTransferred),
    toNum((runtime as any)?.totalBytesTransferred),
    toNum((runtime as any)?.dataBytes),
    toNum(stats.bytesServed),
    toNum(stats.lastBytesServed),
    toNum(stats.bytesOut),
    toNum(stats.totalBytesOut),
    toNum(stats.bytesWritten),
    toNum(stats.totalBytesWritten),
    toNum(stats.bytesTransferred),
    toNum(stats.totalBytesTransferred),
    toNum(stats.dataBytes),
    toNum((health?.metrics as any)?.bytesServed),
    toNum((health?.metrics as any)?.lastBytesServed),
    toNum((health?.metrics as any)?.bytesOut),
    toNum((health?.metrics as any)?.totalBytesOut),
    toNum((health?.metrics as any)?.bytesWritten),
    toNum((health?.metrics as any)?.totalBytesWritten),
    toNum((health?.metrics as any)?.bytesTransferred),
    toNum((health?.metrics as any)?.totalBytesTransferred),
    toNum((health?.raw as any)?.activeSession?.bytesServed),
    toNum((health?.raw as any)?.activeSession?.lastBytesServed),
    toNum((health?.raw as any)?.status?.bytesServed),
    toNum((health?.raw as any)?.status?.lastBytesServed),
    toNum((health?.raw as any)?.bytesServed),
    toNum((health?.raw as any)?.lastBytesServed),
    toNum((health?.raw as any)?.bytesOut),
    toNum((health?.raw as any)?.totalBytesOut),
    toNum((health?.raw as any)?.bytesWritten),
    toNum((health?.raw as any)?.totalBytesWritten),
  );
};

export type DestinationDisplayState = {
  status: DestinationHealthStatus;
  rowLabel: string;
  healthLabel: string;
  colorKey: 'connected' | 'disconnected' | 'disabled';
  waiting: boolean;
  viewerConnected: boolean;
  trafficOk: boolean;
  bitrateOk: boolean;
  fresh: boolean;
  ip: string | null;
  trafficKbps: number;
  bitrateKbps: number;
  updatedAt: string | null;
};

export const getPullHlsDisplayState = (
  output: Transcoder,
  runtime?: PullOutputStatusEnvelope['status'] | HlsSessionStatusEnvelope['activeSession'] | Record<string, any> | null,
): DestinationDisplayState => {
  if (!output.isActive) {
    return {
      status: 'disabled',
      rowLabel: 'Disabled',
      healthLabel: 'Disabled',
      colorKey: 'disabled',
      waiting: false,
      viewerConnected: false,
      trafficOk: false,
      bitrateOk: false,
      fresh: false,
      ip: null,
      trafficKbps: 0,
      bitrateKbps: 0,
      updatedAt: null,
    };
  }

  const metrics = getDestinationHealthMetrics(output, runtime);
  const stats = readRuntimeStats(output, runtime);
  const ip = firstText(
    metrics.ip,
    (runtime as any)?.currentViewer?.ip,
    (runtime as any)?.clientIp,
    (runtime as any)?.viewerIp,
    (runtime as any)?.ip,
    stats.currentViewer?.ip,
    stats.clientIp,
    stats.viewerIp,
    stats.ip,
    stats.remoteIp,
  );
  const trafficKbps = Math.max(
    toNum(metrics.trafficKbps),
    toNum((runtime as any)?.currentKbps),
    toNum((runtime as any)?.averageKbps),
    toNum((runtime as any)?.peakKbps),
  );
  const bitrateKbps = getDestinationBitrateKbps(output, runtime);
  const updatedAt = firstText(
    metrics.updatedAt,
    (runtime as any)?.lastActivityAt,
    (runtime as any)?.lastSeenAt,
    (runtime as any)?.lastViewerSeen,
    (runtime as any)?.currentViewer?.lastSeenAt,
    (runtime as any)?.lastRequestAt,
    (runtime as any)?.lastPlaylistRequestAt,
    (runtime as any)?.lastSegmentRequestAt,
    stats.lastActivityAt,
    stats.lastSeenAt,
    stats.lastViewerSeen,
    stats.currentViewer?.lastSeenAt,
    stats.lastRequestAt,
    stats.lastPlaylistRequestAt,
    stats.lastSegmentRequestAt,
  );
  const viewerConnected = Boolean(ip);
  const isPull = isPullOutput(output);
  const trafficOk = trafficKbps > UI_STATUS_TRAFFIC_KBPS_MIN || (isPull && metrics.dataMoving === true);
  const bitrateOk = isPull || bitrateKbps > UI_STATUS_BITRATE_KBPS_MIN;
  const freshWindowMs = output.protocol === 'hls' ? UI_HLS_STATUS_FRESH_MS : UI_STATUS_FRESH_MS;
  const fresh = isFresh(
    freshWindowMs,
    updatedAt,
  );
  const connected = metrics.status === 'connected';
  const waiting = false;
  return {
    status: connected ? 'connected' : 'disconnected',
    rowLabel: connected ? (isPull ? 'Pulled' : 'Connected') : 'Disconnected',
    healthLabel: connected ? (isPull ? 'Pulled' : 'Connected') : 'Disconnected',
    colorKey: connected ? 'connected' : 'disconnected',
    waiting,
    viewerConnected,
    trafficOk,
    bitrateOk,
    fresh,
    ip,
    trafficKbps,
    bitrateKbps,
    updatedAt,
  };
};

export const getDestinationStatus = (
  output: Transcoder,
  runtime?: PullOutputStatusEnvelope['status'] | HlsSessionStatusEnvelope['activeSession'] | DestinationHealthEnvelope | Record<string, any> | null,
): DestinationHealthStatus => {
  if (!output.isActive) return 'disabled';
  if (isPullOutput(output) || output.protocol === 'hls') return getPullHlsDisplayState(output, runtime as any).status;
  const explicit = (runtime as any)?.status;
  if (explicit === 'connecting' || explicit === 'connected' || explicit === 'disconnected' || explicit === 'disabled') return explicit;
  return getDestinationHealthMetrics(output, runtime).status;
};

export const getPullLiveStatus = (
  output: Transcoder,
  status?: PullOutputStatusEnvelope['status'] | Record<string, any> | null,
) => {
  return getDestinationStatus(output, status);
};

export const getHlsLiveStatus = (session?: HlsSessionStatusEnvelope['activeSession']) => {
  if (!session) return 'idle';
  const state = String((session as any).status || '').toLowerCase();
  if (state === 'expired' || state === 'disconnected' || state === 'offline') return 'idle';

  const hasViewer = Boolean(
    (session as any).clientIp ||
    (session as any).ip ||
    (session as any).remoteIp ||
    (session as any).viewerIp ||
    (session as any).hlsSessionBoundIp ||
    toNum((session as any).viewerCount) > 0 ||
    toNum((session as any).viewers) > 0 ||
    toNum((session as any).clientCount) > 0
  );

  const activity =
    toNum((session as any).requestCount) +
    toNum((session as any).playlistRequests) +
    toNum((session as any).segmentRequests) +
    toNum((session as any).bytesServed) +
    toNum((session as any).lastBytesServed) +
    toNum((session as any).averageKbps) +
    toNum((session as any).peakKbps) +
    toNum((session as any).currentKbps);

  if (hasViewer && activity > 0) return 'connected';

  if (!session.lastSeenAt) return 'idle';
  const lastSeen = new Date(session.lastSeenAt).getTime();
  if (Number.isNaN(lastSeen)) return 'idle';
  return Date.now() - lastSeen <= 15000 ? 'connected' : 'idle';
};

export const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

export const STATUS_COLOR: Record<string, string> = {
  running: 'var(--success)',
  connected: 'var(--success)',
  disconnected: 'var(--danger)',
  connecting: 'var(--warning)',
  retrying: 'var(--danger)',
  error: 'var(--danger)',
  idle: 'var(--text-disabled)',
  disabled: 'var(--text-disabled)',
};

export const labelSx = {
  fontSize: '0.64rem',
  fontWeight: 900,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.disabled',
};

export const consoleSectionHeadingSx = {
  fontSize: '0.68rem',
  fontWeight: 900,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.secondary',
};

export const monoSx = {
  fontFamily: "'DM Mono', monospace",
  fontSize: '0.74rem',
  fontWeight: 500,
};

export const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 0, fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
};

export const smallBtnSx = {
  borderRadius: 0,
  fontSize: '0.72rem',
  textTransform: 'none' as const,
  px: 1.1,
  py: 0.45,
  minWidth: 0,
};

export const consoleIconButtonSx = (theme: Theme) => ({
  borderRadius: 0,
  border: '1px solid',
  borderColor: theme.palette.divider,
  bgcolor: theme.palette.background.paper,
  '&:hover': {
    bgcolor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
});

export const consoleFilterChipSx = (theme: Theme, selected: boolean) => ({
  borderRadius: 0,
  fontSize: '0.72rem',
  fontWeight: 700,
  borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
  bgcolor: selected ? theme.palette.action.selected : theme.palette.background.paper,
});

export const CapacityBar = ({ label, used, limit }: { label: string; used: number; limit: number }) => (
  <Box sx={{ minWidth: 118 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.35 }}>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography sx={{ ...monoSx, fontSize: '0.65rem', color: 'text.disabled' }}>{used}/{limit}</Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={limit > 0 ? Math.min((used / limit) * 100, 100) : 0}
      sx={{ height: 2, borderRadius: 0, bgcolor: 'action.disabledBackground' }}
    />
  </Box>
);

export const StatCell = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ ...monoSx, color: 'text.primary' }} noWrap>{value}</Typography>
  </Box>
);

export const copyText = async (value: string, onDone: () => void) => {
  await navigator.clipboard.writeText(value);
  onDone();
};

export type FilterKey = 'ALL' | 'DIRECT' | 'SOCIAL' | 'SERVICE' | 'MANAGED' | 'PULL' | 'HLS';
